import { TWENTY_BASE_URL } from './config';

// Click-tracking link rewriting for the compose ("Add tracking") flow. Each
// <a href="https://…"> in the draft is rewritten to go through Twenty's public
// track-click route, which records the click on the matching loggedEmail (by
// token) and redirects the recipient to the original destination:
//   https://crm.clswind.com/s/track/c/<token>?u=<base64url(originalHref)>
// The route trigger controller mounts under /s/, so the public prefix is /s.

const TRACK_PREFIX = `${TWENTY_BASE_URL}/s/track/c/`;

// URL-safe base64 of a UTF-8 string. Buffer isn't available in the browser, so
// encode via TextEncoder + btoa and translate to the base64url alphabet (the
// route decodes with Buffer.from(u, 'base64url')).
export const base64UrlEncode = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const trackingUrl = (token: string, href: string): string =>
  `${TRACK_PREFIX}${token}?u=${base64UrlEncode(href)}`;

// Only http/https links are rewritten. mailto:/tel:/#anchors are left alone, and
// a link already pointing at our track route is skipped so re-running the action
// (or editing after tracking) never double-wraps a URL.
const isRewritable = (href: string): boolean =>
  /^https?:\/\//i.test(href) && !href.startsWith(TRACK_PREFIX);

const HREF_RE = /(<a\b[^>]*?\bhref\s*=\s*)("([^"]*)"|'([^']*)')/gi;

// Rewrite every eligible <a href> in an HTML body. Returns the new HTML and how
// many links were rewritten (so the UI can tell the user, and so a body with no
// links can be flagged). Operates on the raw href attribute, preserving the rest
// of the anchor markup.
export const rewriteLinks = (
  html: string,
  token: string,
): { html: string; count: number } => {
  let count = 0;
  const out = html.replace(HREF_RE, (match, prefix, _quoted, dq, sq) => {
    const href = (dq ?? sq ?? '').trim();
    if (!isRewritable(href)) return match;
    count += 1;
    const quote = dq !== undefined ? '"' : "'";
    return `${prefix}${quote}${trackingUrl(token, href)}${quote}`;
  });
  return { html: out, count };
};
